import api from '../../../shared/api/client';
import { parseReportsConfig, serializeReportsConfig } from '../utils/reportFormat';

/** Legacy level → settings_key (kept for backward-compatible reads). */
export const LEGACY_ROLE_KEYS = {
  1: 'seller_reports',
  2: 'supervisor_reports',
  3: 'saleoffice_reports',
  4: 'center_reports',
};

export function jobReportsKey(jobId) {
  return `job_${jobId}_reports`;
}

export function getLegacyRoleKey(profileOrLevel) {
  if (profileOrLevel && typeof profileOrLevel === 'object') {
    const jobId = Number(profileOrLevel.job_id ?? profileOrLevel.job_details?.id);
    if (Number.isFinite(jobId) && jobId >= 70) return 'admin_reports';
    const lt = Number(
      profileOrLevel.job_details?.level_type ?? profileOrLevel.level_type ?? profileOrLevel.job_id,
    );
    return LEGACY_ROLE_KEYS[lt] || 'seller_reports';
  }
  const lt = Number(profileOrLevel);
  if (Number.isFinite(lt) && lt >= 70) return 'admin_reports';
  return LEGACY_ROLE_KEYS[lt] || 'seller_reports';
}

/** Preferred per-job reports settings key. */
export function getRoleKey(profile) {
  const jobId = profile?.job_id ?? profile?.job_details?.id;
  if (jobId != null && jobId !== '') return jobReportsKey(jobId);
  return getLegacyRoleKey(profile);
}

/** Fetch one settings row; null when missing (404), throw on real failures. */
async function fetchReportsSettingsKey(key) {
  try {
    const res = await api.get(`/settings/${key}`);
    return {
      key,
      data: res.data,
      parsed: parseReportsConfig(res.data?.settings_json),
    };
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

/**
 * Load reports config for a job: try job_{id}_reports first, then legacy level key.
 */
export async function loadReportsConfigForJob(jobId, levelType) {
  const primary = jobReportsKey(jobId);
  const primaryResult = await fetchReportsSettingsKey(primary);
  if (primaryResult) return primaryResult;

  const legacy = getLegacyRoleKey(levelType);
  if (legacy !== primary) {
    const legacyResult = await fetchReportsSettingsKey(legacy);
    if (legacyResult) return legacyResult;
  }

  return {
    key: primary,
    data: null,
    parsed: parseReportsConfig(null),
  };
}

export async function loadReportsConfigForProfile(profile) {
  const jobId = profile?.job_id ?? profile?.job_details?.id;
  const levelType =
    profile?.job_details?.level_type ?? profile?.level_type ?? profile?.job_id;
  if (jobId == null || jobId === '') {
    const legacy = getLegacyRoleKey(profile);
    const legacyResult = await fetchReportsSettingsKey(legacy);
    if (legacyResult) return legacyResult;
    return { key: legacy, data: null, parsed: parseReportsConfig(null) };
  }
  return loadReportsConfigForJob(jobId, levelType);
}

export const reportsApi = {
  run: (widget, period, config) =>
    api.post('/reports/run', { widget, period }, config),
  runBatch: (queries, period, config) =>
    api.post('/reports/run-batch', { queries, period }, config),
  columns: (table) => api.get(`/reports/columns/${table}`),
  tables: () => api.get('/reports/tables'),
  discoverSql: (sql, period) => api.post('/reports/sql/discover', { sql, period }),
  sqlParams: () => api.get('/reports/sql/params'),
  getConfig: (roleKey) => api.get(`/settings/${roleKey}`),
  saveConfig: (roleKey, config) =>
    api.post('/settings', {
      settings_key: roleKey,
      settings_type: 'reports',
      settings_json: serializeReportsConfig(
        Array.isArray(config) ? { reports: config } : config,
      ),
      is_active: true,
    }),
};

export { parseReportsConfig, serializeReportsConfig };
export { resolveMainDashboardReport, BUILTIN_CUSTOMER_TARGET_ID } from '../utils/reportFormat';
